interface CaptchaWidget {
    execute(): void;
    reset(): void;
}

declare global {
    interface Window {
        onCaptchaStarted?: () => void;
        onCaptchaFinished?: (widget: CaptchaWidget) => void;
        privateCaptcha?: {
            render(element: HTMLElement): CaptchaWidget;
        };
    }
}

let isCaptchaSolved = false;
let captchaInProgress = false;
let captchaWidget: CaptchaWidget | null = null;

type FormField = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

interface ValidationRule {
    validate(field: FormField, form: HTMLFormElement): boolean;
    message: string;
}

// Swedish error messages for native constraint violations (HTML5 attributes on the form fields).
const nativeMessages: Record<string, Partial<Record<keyof ValidityState, string>>> = {
    ContactMessage: {
        valueMissing: 'Det här fältet är obligatoriskt.',
        tooShort: 'Ange minst 2 tecken.',
        tooLong: 'Ange högst 10000 tecken.',
    },
    ContactAddress: {
        typeMismatch: 'Ange en giltig e-postadress.',
    },
    Subject: {
        valueMissing: 'Det här fältet är obligatoriskt.',
    },
};

// Custom rules for constraints the Constraint Validation API cannot express
const customRules: Record<string, ValidationRule[]> = {
    ContactAddress2: [
        {
            validate: (f, form) => {
                if (f.value === '') return true;
                const address = (form.elements.namedItem('ContactAddress') as HTMLInputElement)?.value ?? '';
                return f.value === address;
            },
            message: 'Behöver vara samma e-postadress som i fältet ovan.',
        },
    ],
    FormFiles: [
        {
            validate: (f) => {
                const files = (f as HTMLInputElement).files;
                if (!files || files.length === 0) return true;
                return files.length <= 3;
            },
            message: 'Du kan bifoga högst tre filer.',
        },
        {
            validate: (f) => {
                const files = (f as HTMLInputElement).files;
                if (!files || files.length === 0) return true;
                const allowed = ((f as HTMLInputElement).accept || '')
                    .split(',')
                    .map((t) => t.trim())
                    .filter(Boolean);
                return Array.from(files).every((file) => allowed.includes(file.type));
            },
            message: 'En av filerna är av fel format (jpeg, png, heic, mp4 samt mov är tillåtna).',
        },
    ],
};

const fieldsToValidate = new Set([...Object.keys(nativeMessages), ...Object.keys(customRules)]);

function showError(field: HTMLElement, message: string): void {
    clearError(field);
    field.classList.add('error');

    const label = document.createElement('label');
    label.className = 'error';
    label.htmlFor = field.id || '';
    label.dataset.validationFor = field.getAttribute('name') || '';
    label.textContent = message;
    field.insertAdjacentElement('afterend', label);
}

function clearError(field: HTMLElement): void {
    field.classList.remove('error');
    const name = field.getAttribute('name') || '';
    field.parentElement?.querySelector<HTMLElement>(`label.error[data-validation-for="${name}"]`)?.remove();
}

function validateField(field: FormField, form: HTMLFormElement): boolean {
    // Check native constraint validation (required, minlength, type="email", etc.)
    if (!field.checkValidity()) {
        const messages = nativeMessages[field.name];
        if (messages) {
            for (const [key, message] of Object.entries(messages)) {
                if (field.validity[key as keyof ValidityState]) {
                    showError(field, message);
                    return false;
                }
            }
        }
        showError(field, field.validationMessage);
        return false;
    }

    // Check custom rules for things the Constraint Validation API cannot express
    const rules = customRules[field.name];
    if (rules) {
        for (const rule of rules) {
            if (!rule.validate(field, form)) {
                showError(field, rule.message);
                return false;
            }
        }
    }

    clearError(field);
    return true;
}

function validateForm(form: HTMLFormElement): boolean {
    let isValid = true;
    for (const name of fieldsToValidate) {
        const field = form.elements.namedItem(name);
        if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement) {
            if (!validateField(field, form)) {
                isValid = false;
            }
        }
    }
    return isValid;
}

function getSubmitButton(): HTMLButtonElement | null {
    return document.getElementById('contact-submit') as HTMLButtonElement | null;
}

function renderCaptchaWidget(): CaptchaWidget | null {
    const element = document.querySelector<HTMLElement>('.private-captcha');
    if (element && window.privateCaptcha) {
        return window.privateCaptcha.render(element);
    }
    return null;
}

// Global callbacks required by PrivateCaptcha widget (referenced via data-attributes in the Razor view)
window.onCaptchaStarted = (): void => {
    captchaInProgress = true;
    const btn = getSubmitButton();
    if (btn) {
        btn.disabled = true;
        btn.textContent = 'Meddelandet skickas...';
    }
};

window.onCaptchaFinished = (widget: CaptchaWidget): void => {
    isCaptchaSolved = true;
    captchaInProgress = false;

    const form = document.querySelector<HTMLFormElement>('form');
    if (form && validateForm(form)) {
        form.submit();
    } else {
        isCaptchaSolved = false;
        widget.reset();
        const btn = getSubmitButton();
        if (btn) {
            btn.disabled = false;
            btn.textContent = 'Skicka';
        }
    }
};

document.addEventListener('DOMContentLoaded', () => {
    const form = document.querySelector<HTMLFormElement>('form');
    if (!form) return;

    // Validate individual fields on focusout
    form.addEventListener('focusout', (e) => {
        const target = e.target;
        if (
            (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) &&
            !target.closest('.private-captcha')
        ) {
            validateField(target, form);
        }
    });

    // Handle form submission: validate then trigger captcha
    form.addEventListener('submit', (e) => {
        e.preventDefault();

        if (!validateForm(form)) return;

        if (!captchaWidget) {
            captchaWidget = renderCaptchaWidget();
        }

        if (!isCaptchaSolved && !captchaInProgress) {
            captchaWidget?.execute();
            return;
        }

        if (isCaptchaSolved) {
            form.submit();
        }
    });
});

export {};
